import { AUDIO_POOL, stationRegion, guessUserRegion } from './sources.js';

/**
 * AudioManager
 * ─────────────────────────────────────────────────────────────
 * Single <audio> element wired up to a flat pool of streams + local files.
 *
 *  - Disabled by default. Browsers require a user gesture before audio
 *    can play, so the user must click the toggle button at least once.
 *  - rotate() picks a random source different from the current one.
 *    On error (dead stream, CORS, 404), the failed source is blacklisted
 *    and another one is auto-picked.
 *  - User preference (enabled + last source) persists in localStorage so
 *    a returning visitor with sound on stays with sound on.
 *
 * Public API:
 *   setEnabled(bool)        // explicit on/off
 *   toggle()                // returns new enabled state
 *   rotate()                // pick a new random source, returns it
 *   setVolume(0..1)
 *   isEnabled, currentSource, audioElement (read-only getters)
 */

const STORAGE_KEY = 'sb:audio-prefs';
const DEFAULT_VOLUME = 0.4;
const MAX_RETRY_PER_ROTATE = 5;

export class AudioManager {
  constructor({ pool = AUDIO_POOL, volume = DEFAULT_VOLUME } = {}) {
    this._pool = pool.slice();
    this._failed = new Set();
    this._currentSrc = null;
    this._enabled = false;
    this._listeners = new Set();
    this._destroyed = false;

    this.audioEl = new Audio();
    this.audioEl.preload = 'none';      // don't fetch unless we play
    this.audioEl.loop = false;          // streams don't end; files just fade out
    this.audioEl.volume = volume;
    // The user's level. The element's volume is transient (fades), so prefs
    // must always be written from this, never from audioEl.volume.
    this._prefVolume = volume;

    // crossOrigin OFF intentionally — many radio streams don't send CORS
    // headers and would refuse to play with crossOrigin="anonymous".
    // We're not analyzing audio, just playing it.

    this.audioEl.addEventListener('error', () => this._onError(), { passive: true });
    // `stalled` is NOT failure — Chrome fires it after ~3s without bytes,
    // which is routine on a slow link. Just keep the watchdog armed.
    this.audioEl.addEventListener('stalled', () => { if (!this._watchdog) this._armWatchdog(); }, { passive: true });
    // Watchdog: a stream that connects but never delivers audio fires no
    // `error` — the chip would name it while the page sits silent forever.
    this.audioEl.addEventListener('playing', () => this._clearWatchdog(), { passive: true });

    this._restorePrefs();
  }

  // ── public API ──────────────────────────────────────────────────────

  get isEnabled() { return this._enabled; }
  get currentSource() { return this._currentSrc; }
  get audioElement() { return this.audioEl; }

  setEnabled(enabled) {
    enabled = !!enabled;
    this._wasEnabled = false;            // a manual toggle cancels any pending resume
    if (enabled === this._enabled) return enabled;
    this._enabled = enabled;
    this._savePrefs();

    if (this._tape) {
      // Tape mode: the toggle mutes/unmutes the clip, the radio stays parked.
      this._tape.muted = !enabled;
      this._tape.volume = enabled ? this._prefVolume : 0;
    } else if (enabled) {
      if (!this._currentSrc) {
        this.rotate();
      } else {
        this._tryPlay();
      }
    } else {
      this.audioEl.pause();
    }

    this._emit();
    return enabled;
  }

  toggle() {
    return this.setEnabled(!this._enabled);
  }

  /**
   * Pick a new source different from the current one and (if enabled)
   * start playing it. Returns the chosen source path.
   */
  rotate() {
    let attempts = 0;
    let next = this._pickNext();
    while (next && attempts < MAX_RETRY_PER_ROTATE && next === this._currentSrc) {
      next = this._pickNext();
      attempts++;
    }
    if (!next) return null;

    this._currentSrc = next;
    this._savePrefs();
    this.audioEl.src = next;
    this.audioEl.load();

    if (this._enabled && !this._tape) this._tryPlay();
    this._emit();
    return next;
  }

  /**
   * Tape audio: let a clip's own soundtrack play instead of the station.
   * Pass the <video> (unmuted here at `volume`) or null to hand the air
   * back to the radio. Reported through onChange as `tape: {name}`.
   */
  duck(videoEl, { name = 'TAPE', volume = this._prefVolume } = {}) {
    if (this._tape && this._tape !== videoEl) { this._tape.muted = true; this._tape.volume = 0; }
    this._tape = videoEl || null;
    this._tapeName = name;
    if (videoEl) {
      this.audioEl.pause();
      videoEl.muted = !this._enabled;
      videoEl.volume = this._enabled ? volume : 0;
    } else if (this._enabled && this._currentSrc) {
      this._tryPlay();
    }
    this._emit();
  }
  get isOnTape() { return !!this._tape; }

  /**
   * Fade everything on air (station or tape) to silence over `ms`, then
   * switch off. Returns a promise that resolves when silent. The volume
   * preference is restored so a later enable comes back at normal level.
   */
  fadeOut(ms = 900) {
    const el = this._tape || this.audioEl;
    const v0 = el === this.audioEl ? this._prefVolume : el.volume;
    this._wasEnabled = this._enabled;
    return new Promise((resolve) => {
      const t0 = performance.now();
      const step = () => {
        const k = Math.min(1, (performance.now() - t0) / ms);
        el.volume = v0 * (1 - k) * (1 - k);
        if (k < 1) requestAnimationFrame(step);
        else {
          el.volume = v0;                 // restore BEFORE anything can save prefs
          const was = this._wasEnabled;
          this.setEnabled(false);
          this._wasEnabled = was;
          resolve();
        }
      };
      step();
    });
  }

  /** Bring the air back if fadeOut() turned it off. */
  resumeAfterFade() {
    if (this._wasEnabled) this.setEnabled(true);
    this._wasEnabled = false;
  }

  setVolume(v) {
    this._prefVolume = Math.max(0, Math.min(1, v));
    this.audioEl.volume = this._prefVolume;
    this._savePrefs();
  }

  /**
   * Fire-and-forget short SFX (channel-change blip, etc).
   * Plays *over* the current stream without interrupting it.
   *  - No-op if audio is disabled (we never bug the user when sound is off).
   *  - Silently swallows missing-file errors so an absent ./sounds/channel.mp3
   *    doesn't pollute the console — drop the file in to enable.
   */
  playEffect(src, { volume = 0.55 } = {}) {
    if (!this._enabled || !src) return;
    const fx = new Audio();
    fx.preload = 'auto';
    fx.volume = Math.max(0, Math.min(1, volume));
    fx.addEventListener('error', () => { /* missing or unplayable — fine */ }, { once: true });
    // Release the reference once playback ends so the GC can clean up.
    fx.addEventListener('ended', () => { fx.src = ''; }, { once: true });
    fx.src = src;
    fx.play?.().catch(() => {});
  }

  /** Subscribe to state changes. Returns an unsubscribe fn. */
  onChange(fn) {
    this._listeners.add(fn);
    return () => this._listeners.delete(fn);
  }

  destroy() {
    this._destroyed = true;
    this._listeners.clear();
    this.audioEl.pause();
    this.audioEl.removeAttribute('src');
    this.audioEl.load();
  }

  // ── internal ────────────────────────────────────────────────────────

  _pickNext() {
    const usable = this._pool.filter((s) => !this._failed.has(s));
    if (!usable.length) {
      // Everything failed at least once — wipe blacklist and retry.
      this._failed.clear();
      return this._pool[(Math.random() * this._pool.length) | 0] || null;
    }

    // Geo-weighted pick: stations in the listener's region (from their
    // timezone, no IP lookup) are 3× as likely, global signals 2×, the
    // rest 1× — nearby flavor without losing the surprise of the dial.
    if (!this._userRegion) this._userRegion = guessUserRegion();
    const weights = usable.map((s) => {
      const r = stationRegion(s);
      if (r === this._userRegion) return 3;
      if (r === 'global') return 2;
      return 1;
    });
    let roll = Math.random() * weights.reduce((a, b) => a + b, 0);
    for (let i = 0; i < usable.length; i++) {
      roll -= weights[i];
      if (roll <= 0) return usable[i];
    }
    return usable[usable.length - 1];
  }

  _tryPlay() {
    // play() returns a promise that rejects on autoplay policy violation
    // or unresolvable streams. Swallow silently — error handler covers the
    // dead-stream case via the 'error' event; the watchdog covers hangs.
    this.audioEl.play?.().catch(() => {});
    this._armWatchdog();
  }

  static WATCHDOG_MS = 15_000;   // give a slow relay a real chance
  static WATCHDOG_MAX_MS = 40_000;

  _armWatchdog(elapsed = 0) {
    this._clearWatchdog();
    const src = this._currentSrc;
    const el = this.audioEl;
    this._watchdog = setTimeout(() => {
      this._watchdog = null;
      if (this._destroyed || !this._enabled || this._tape || this._currentSrc !== src) return;
      const playing = !el.paused && el.readyState >= 3 && el.currentTime > 0;
      if (playing) return;
      const total = elapsed + AudioManager.WATCHDOG_MS;
      // Still connecting / buffering (bytes are arriving): be patient, up to the max.
      const progressing = el.networkState === 2 && (el.readyState > 0 || el.buffered.length > 0);
      if (progressing && total < AudioManager.WATCHDOG_MAX_MS) {
        console.info('[AudioManager] slow stream, still waiting:', src);
        el.play?.().catch(() => {});
        this._armWatchdog(total);
        return;
      }
      console.warn('[AudioManager] no audio from', src, 'after', total / 1000, 's — skipping');
      this._onError();
    }, AudioManager.WATCHDOG_MS);
  }

  _clearWatchdog() { clearTimeout(this._watchdog); this._watchdog = null; }

  _onError() {
    if (this._destroyed || !this._currentSrc) return;
    this._failed.add(this._currentSrc);
    if (this._enabled) {
      // Auto-skip to the next one when a stream dies mid-listen.
      this.rotate();
    }
  }

  _emit() {
    const state = {
      enabled: this._enabled,
      source: this._currentSrc,
      tape: this._tape ? { name: this._tapeName } : null,
    };
    this._listeners.forEach((fn) => {
      try { fn(state); } catch { /* listener errors shouldn't blow us up */ }
    });
  }

  // ── persistence ─────────────────────────────────────────────────────

  _restorePrefs() {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      const prefs = JSON.parse(raw);
      // Ignore a stored 0 — an old bug saved the mid-fade level; a muted
      // preference is expressed by `enabled`, never by volume.
      if (typeof prefs.volume === 'number' && prefs.volume > 0.02) {
        this._prefVolume = prefs.volume;
        this.audioEl.volume = prefs.volume;
      }
      // Don't auto-enable on load — browsers will reject playback without a
      // fresh user gesture. We DO restore the source so the first toggle
      // click resumes the same station the user picked last time.
      if (typeof prefs.source === 'string' && this._pool.includes(prefs.source)) {
        this._currentSrc = prefs.source;
        this.audioEl.src = prefs.source;
      }
      // Remember intent so we can show the right icon state at boot.
      this._wantsEnabled = !!prefs.enabled;
    } catch {
      /* malformed prefs — ignore */
    }
  }

  _savePrefs() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        enabled: this._enabled,
        source: this._currentSrc,
        volume: this._prefVolume,
      }));
    } catch {
      /* localStorage may be disabled (private mode) — ignore */
    }
  }
}
